import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, Link, useLocation, useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import imageCompression from 'browser-image-compression';
import { isEmpty, getSelectedCompany, getSelectedOffice, removeSpace } from "../../Global/CustomFunctions";
import { LocalStorage } from "../../Global";
import GeneralNotification from "../GeneralNotification";
import LanguageSelector from "./LanguageSelector";
import ApprovalBadge from "../ApprovalBadge";
import { getPreSignedUrl, uploadImgToS3, updateCompanyLogo } from "../../../utils/common";
import { Common } from "../../../utils";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import ChangePasswordModal from "./ChangePasswordModal";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";
import { getUserFromStorage, navigateToMyCalendar, navigateToWallChart } from "../../../utils/workplaceNavigation";

import "./settings/Settings.css";

// Default logo path - will be used as fallback
const DEFAULT_LOGO = "/assets/img/logo-default.png";

const { Company: CompanyApi } = config.REST_API;

/** Logo URL from Company/GetSettings (handles typical API casing). */
const getLogoUrlFromSettings = (data) => {
  if (!data || typeof data !== "object") return "";
  const raw =
    data.companyLogoUrl ||
    data.CompanyLogoUrl ||
    data.logo ||
    data.Logo ||
    data.logoUrl ||
    data.LogoUrl;
  const s = raw != null ? String(raw).trim() : "";
  return s;
};

export const Header = ({ isLoading, isLoggedIn, clickLogOut }) => {
  const { t } = useTranslation();
  const history = useHistory();
  let user;
  if (!isEmpty(localStorage.userInfo)) {
    user = JSON.parse(localStorage.userInfo);
  }

  // will work on it in future
  // const options = [
  //   { key: "pak", text: "PAK Office", value: "pk" },
  //   { key: "uk", text: "UK Office", value: "uk" }
  // ];

  const [showPopover, setShowPopover] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const togglePopover = () => {
    setShowPopover(!showPopover);
  };

  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [isUploading, setIsUploading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const inputRef = useRef(null);

  // Check if current user is admin
  const isAdmin = () => {
    try {
      let roleId;
      try {
        const { WebStorage, WebStorageNames } = require("../../../utils");
        roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
      } catch (e) {
        roleId = localStorage.getItem("roleId");
      }
      if (roleId) {
        roleId = parseInt(roleId);
        // Admin roles are typically 1 (Super Admin) or 2 (Admin)
        return roleId === 1 || roleId === 2;
      }
      return false;
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("headerCheckAdminStatus", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Request failed",
        });
      }
      return false;
    }
  };

  // Check if current user is admin or manager
  const isAdminOrManager = () => {
    try {
      let roleId;
      try {
        const { WebStorage, WebStorageNames } = require("../../../utils");
        roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
      } catch (e) {
        roleId = localStorage.getItem("roleId");
      }
      if (roleId) {
        roleId = parseInt(roleId);
        // Admin roles: 1 (Super Admin) or 2 (Admin), Manager: 3
        return roleId === 1 || roleId === 2 || roleId === 3;
      }
      return false;
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("headerCheckAdminManagerStatus", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Request failed",
        });
      }
      return false;
    }
  };

  // Load logo: localStorage (login payload / admin upload), else fetch GetSettings for roles whose companyInfo omits logo
  useEffect(() => {
    let cancelled = false;

    const applyLogoFromStorageOrApi = async () => {
      if (!isLoggedIn) return;

      try {
        const selectedCompanyRaw = localStorage.getItem("selectedCompany");
        if (!selectedCompanyRaw) return;

        const company = JSON.parse(selectedCompanyRaw);
        const storedLogo =
          company.logo != null && String(company.logo).trim() !== ""
            ? String(company.logo).trim()
            : "";

        if (storedLogo) {
          if (!cancelled) setLogoUrl(storedLogo);
          return;
        }

        if (!company.id) return;

        const response = await axiosBasic.get(CompanyApi.GetSettings);
        const { success, data } = response.data || {};
        if (!success || !data || cancelled) return;

        const fromSettings = getLogoUrlFromSettings(data);
        if (!fromSettings) return;

        if (!cancelled) setLogoUrl(fromSettings);
        company.logo = fromSettings;
        localStorage.setItem("selectedCompany", JSON.stringify(company));
      } catch (error) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("headerLoadLogo", {
            type: "exception",
            success: false,
            reason: error?.message ?? "Error loading logo",
          });
        }
      }
    };

    applyLogoFromStorageOrApi();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // Get company ID from localStorage
  const getCompanyId = () => {
    try {
      const selectedCompany = localStorage.getItem('selectedCompany');
      if (selectedCompany) {
        const company = JSON.parse(selectedCompany);
        return company.id;
      }
      return null;
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("headerGetCompanyId", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Request failed",
        });
      }
      return null;
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      let optimizedFile = null; // Declare outside try block

      try {
        setIsUploading(true);

        // Validate file type
        if (!file.type.startsWith('image/')) {
          Common.showErrorMessage('Please select a valid image file');
          return;
        }

        // Validate file size (max 10MB for original file)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          Common.showErrorMessage('Image size should be less than 10MB');
          return;
        }

        optimizedFile = await optimizeImage(file);

        // Show optimization results
        const compressionRatio = ((file.size - optimizedFile.size) / file.size * 100).toFixed(1);
        if (compressionRatio > 10) {
          console.log(`Image optimized! Size reduced by ${compressionRatio}%`);
        }

        // Generate unique filename with .jpg extension for optimized images
        const timestamp = new Date().getTime();
        const fileName = `company-logo-${timestamp}.jpg`;

        // Get presigned URL
        const presignedUrlResponse = await getPreSignedUrl(fileName, "Logo");
        console.log('Presigned URL Response:', presignedUrlResponse);

        // Handle different response structures
        let presignedUrl;
        if (presignedUrlResponse && presignedUrlResponse.data) {
          presignedUrl = presignedUrlResponse.data;
        } else if (presignedUrlResponse && typeof presignedUrlResponse === 'string') {
          presignedUrl = presignedUrlResponse;
        } else if (presignedUrlResponse && presignedUrlResponse.success && presignedUrlResponse.data) {
          presignedUrl = presignedUrlResponse.data;
        } else {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("headerLogoPresignedUrl", {
              type: "exception",
              success: false,
              reason: "Invalid presigned URL response structure",
            });
          }
          throw new Error('Invalid presigned URL response');
        }

        if (!presignedUrl) {
          throw new Error('Failed to get presigned URL');
        }

        // Upload to S3
        const uploadedUrl = await uploadImgToS3('PUT', presignedUrl.data, optimizedFile);

        // Update logo URL with the uploaded image
        setLogoUrl(uploadedUrl);

        // Update company logo URL in backend
        const companyId = getCompanyId();
        if (companyId) {
          try {
            const response = await updateCompanyLogo(companyId, uploadedUrl);
            if (response.data && response.data.success) {
              Common.showSuccessMessage('Logo uploaded and updated successfully!');

              // Update selectedCompany.logo in localStorage
              try {
                const selectedCompany = localStorage.getItem('selectedCompany');
                if (selectedCompany) {
                  const company = JSON.parse(selectedCompany);
                  company.logo = uploadedUrl;
                  localStorage.setItem('selectedCompany', JSON.stringify(company));
                }
              } catch (localStorageError) {
                if (typeof posthog !== "undefined" && posthog.capture) {
                  posthog.capture("headerLogo", {
                    type: "exception",
                    success: false,
                    reason: localStorageError?.message ?? "Error updating selectedCompany in localStorage",
                  });
                }
              }


            } else {
              if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("headerLogoBackend", {
                  type: "exception",
                  success: false,
                  reason: "Failed to update company logo in backend",
                });
              }
              Common.showErrorMessage('Logo uploaded but failed to update in system. Please try again.');
            }
          } catch (backendError) {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("headerLogoBackend", {
                type: "exception",
                success: false,
                reason: backendError?.response?.data?.message ?? backendError?.message ?? "Request failed",
                status: backendError?.response?.status,
              });
            }
            Common.showErrorMessage('Logo uploaded but failed to update in system. Please try again.');
          }
        } else {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("headerLogo", {
              type: "exception",
              success: false,
              reason: "Company ID not found in user info",
            });
          }
          Common.showErrorMessage('Company information not found. Please log in again.');
        }
      } catch (error) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("headerLogoUpload", {
            type: "exception",
            success: false,
            reason: error?.response?.data?.message ?? error?.message ?? "Upload failed",
            status: error?.response?.status,
          });
        }
        Common.showErrorMessage('Failed to upload image. Please try again.');
      } finally {
        setIsUploading(false);
        // Reset input value to allow selecting the same file again
        e.target.value = '';
      }
    }
  };

  const handlePencilClick = () => {
    inputRef.current.click();
  };

  // Image optimization function
  const optimizeImage = async (file) => {
    try {
      setIsOptimizing(true);
      setOptimizationProgress(0);

      console.log('Original file size:', file.size, 'bytes');

      const options = {
        maxSizeMB: 1, // Maximum 1MB
        maxWidthOrHeight: 300, // Smaller size for logos
        useWebWorker: true,
        fileType: 'image/jpeg', // Convert to JPEG for better compression
        quality: 0.9, // Higher quality for logos
        alwaysKeepResolution: false, // Allow resolution reduction for better compression
        onProgress: (progress) => {
          setOptimizationProgress(progress);
        }
      };

      const compressedFile = await imageCompression(file, options);

      setOptimizationProgress(100);
      setIsOptimizing(false);

      return compressedFile;
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("headerImageOptimization", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Image optimization failed",
        });
      }
      setIsOptimizing(false);
      setOptimizationProgress(0);
      // Return original file if optimization fails
      return file;
    }
  };





  const location = useLocation();
  const { companyName, officeName } = useParams();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("default");

  const isLoginOrRegisterPage =
    location.pathname === "/auth" || location.pathname === "/login" || location.pathname === "/register";

  const wallChartActive =
    location.pathname === "/wallchart" || location.pathname.includes("/time-off") || location.pathname.includes("/timeOff");
  const myCalendarActive = location.pathname.includes("/dashboard/calender");

  const handleWallChartClick = (e) => {
    e.preventDefault();
    withWorkplace(() => navigateToWallChart(history, user || getUserFromStorage()));
  };

  const handleMyCalendarClick = (e) => {
    e.preventDefault();
    withWorkplace(() => navigateToMyCalendar(history, user || getUserFromStorage()));
  };

  return (
    <header>
      {!isLoggedIn ? (
        <>
        </>
      ) : (
        <div className="header-container">
          <div className="clear">
            <div className="nav-brand d-flex align-items-center mt-4">
              <Link to="/dashboard">
                <div style={{ position: 'relative' }}>
                  <img
                    // style={{ maxHeight: "70px", width: "200px" }}
                    style={{
                      objectFit: logoUrl === DEFAULT_LOGO ? "contain" : "cover",
                      objectPosition: "center",
                      maxHeight: logoUrl === DEFAULT_LOGO ? "80px" : "70px",
                      width: logoUrl === DEFAULT_LOGO ? "250px" : "200px",
                      opacity: isUploading ? 0.7 : 1,
                    }}
                    className="logo header-logo me-3"
                    src={logoUrl || DEFAULT_LOGO}
                    alt="Time Off Ledger"
                  />
                  {(isUploading || isOptimizing) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        zIndex: 10,
                        textAlign: 'center',
                        minWidth: '120px',
                      }}
                    >
                      {isOptimizing ? (
                        <div>
                          <div>{t("app.header.optimizing")}</div>
                          <div style={{
                            width: '100%',
                            height: '4px',
                            background: '#444',
                            borderRadius: '2px',
                            marginTop: '4px'
                          }}>
                            <div style={{
                              width: `${optimizationProgress}%`,
                              height: '100%',
                              background: '#4CAF50',
                              borderRadius: '2px',
                              transition: 'width 0.3s ease'
                            }}></div>
                          </div>
                          <div style={{ fontSize: '10px', marginTop: '2px' }}>
                            {optimizationProgress.toFixed(0)}%
                          </div>
                        </div>
                      ) : (
                        <div>{t("app.header.uploading")}</div>
                      )}
                    </div>
                  )}
                </div>
              </Link>
              {isAdmin() && (
                <>
                  <i
                    className={`pencil alternate icon cursor-pointer text-custom-primary mb-2 ${(isUploading || isOptimizing) ? 'disabled' : ''}`}
                    onClick={(isUploading || isOptimizing) ? null : handlePencilClick}
                    style={{
                      opacity: (isUploading || isOptimizing) ? 0.5 : 1,
                      cursor: (isUploading || isOptimizing) ? 'not-allowed' : 'pointer'
                    }}
                    title={(isUploading || isOptimizing) ? t("app.header.processing") : t("app.header.changeLogo")}
                  ></i>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e)}
                    style={{ display: "none" }}
                    ref={inputRef}
                    disabled={isUploading || isOptimizing}
                  />
                </>
              )}
            </div>
            <div className="nav-user mt-4 me-3">
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                className="user-details clear"
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <NavLink
                    to="/wallchart"
                    onClick={handleWallChartClick}
                    className={`nav-link active-link header-main-nav-link ${wallChartActive ? "active" : "text-dark"}`}
                    style={{
                      fontWeight: wallChartActive ? "600" : "lighter",
                      color: wallChartActive ? "#2563eb" : undefined,
                      borderBottom: wallChartActive ? "2px solid #2563eb" : "none",
                      paddingBottom: wallChartActive ? "4px" : "0",
                      paddingLeft: "0.5rem",
                      paddingRight: "0.5rem",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                  >
                    Wall Chart
                  </NavLink>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <a
                    href="#"
                    onClick={handleMyCalendarClick}
                    className={`nav-link active-link header-main-nav-link ${myCalendarActive ? "active" : "text-dark"}`}
                    style={{
                      fontWeight: myCalendarActive ? "600" : "lighter",
                      color: myCalendarActive ? "#2563eb" : undefined,
                      borderBottom: myCalendarActive ? "2px solid #2563eb" : "none",
                      paddingBottom: myCalendarActive ? "4px" : "0",
                      paddingLeft: "0.5rem",
                      paddingRight: "0.5rem",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    My Calendar
                  </a>
                </div>

                {isAdminOrManager() && (
                  <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <NavLink
                      to="/approvals"
                      className={`nav-link active-link header-main-nav-link ${location.pathname === "/approvals" ? "active" : "text-dark"
                        }`}
                      style={{
                        fontWeight: location.pathname === "/approvals" ? "600" : "lighter",
                        color: location.pathname === "/approvals" ? "#667eea" : undefined,
                        borderBottom: location.pathname === "/approvals" ? "2px solid #667eea" : "none",
                        paddingBottom: location.pathname === "/approvals" ? "4px" : "0",
                        paddingLeft: "0.5rem",
                        paddingRight: "0.5rem",
                        transition: "all 0.3s ease"
                      }}
                    >
                      Approvals
                    </NavLink>
                    <div style={{ position: "absolute", top: "-5px", right: "-5px" }}>
                      <ApprovalBadge />
                    </div>
                  </div>
                )}

                {user?.approverId && user.approverId !== "" && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <NavLink
                      to="/self-requests"
                      className={`nav-link active-link header-main-nav-link ${location.pathname === "/self-requests" ? "active" : "text-dark"
                        }`}
                      style={{
                        fontWeight: location.pathname === "/self-requests" ? "600" : "lighter",
                        color: location.pathname === "/self-requests" ? "#667eea" : undefined,
                        borderBottom: location.pathname === "/self-requests" ? "2px solid #667eea" : "none",
                        paddingBottom: location.pathname === "/self-requests" ? "4px" : "0",
                        paddingLeft: "0.5rem",
                        paddingRight: "0.5rem",
                        transition: "all 0.3s ease"
                      }}
                    >
                      My Leave Requests
                    </NavLink>
                  </div>
                )}

                <div>
                  <LanguageSelector />
                </div>

                <div>
                  <GeneralNotification />
                </div>

                <div className="dropdown">
                  <button
                    className="btn text-light bg-secondary"
                    type="button"
                    id="responsiveDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {(user?.name || user?.Name || "?").charAt(0).toUpperCase()}
                  </button>
                  <ul
                    className="dropdown-menu dropdown-menu-end dropdown-menu-lg-start"
                    aria-labelledby="responsiveDropdown"
                  >
                    <li style={{ listStyle: "none" }}>
                      <a
                        style={{ textDecoration: "none", cursor: "pointer" }}
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push("/profile");
                        }}
                      >
                        {t("app.header.myDetails")}
                      </a>
                    </li>
                    <li style={{ listStyle: "none" }}>
                      <a
                        style={{ textDecoration: "none", cursor: "pointer" }}
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push("/help-support");
                        }}
                      >
                        {t("app.header.helpAndSupport")}
                      </a>
                    </li>
                    <li style={{ listStyle: "none" }}>
                      <a
                        style={{ textDecoration: "none", cursor: "pointer" }}
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowChangePasswordModal(true);
                        }}
                      >
                        {t("app.header.changePassword")}
                      </a>
                    </li>
                    <li style={{ listStyle: "none" }}>
                      <a
                        href="/auth"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isLoading && typeof clickLogOut === "function") {
                            clickLogOut();
                          }
                        }}
                        style={{ cursor: "pointer", textDecoration: "none" }}
                      >
                        {t("app.header.logout")}
                      </a>
                    </li>
                  </ul>
                </div>

                {/* will work on it in future */}
                {/* <span>
                  <Button.Group color="teal">
                    <Dropdown
                      options={options}
                      floating
                      button
                      className="icon"
                      placeholder={t("app.header.selectLocation")}
                    />
                  </Button.Group>
                </span> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />

      {workplaceRequiredModal}
    </header>
  );
};
